import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { CornerLeftUp, Folder, HardDrive } from "lucide-react";
import { nginxBrowseDirs } from "../../redux/actionCreator";

/**
 * Pick a directory on the server to serve a location from.
 *
 * Directories only — the picker never lists files, because the thing being
 * chosen is a document root. Starts at a few places an nginx root usually
 * lives rather than at `/`, which saves four clicks every time.
 */
const SHORTCUTS = ["/var/www", "/srv", "/opt", "/home", "/usr/share/nginx/html", "/"];

export default function FolderPickerModal({ projectId, serverId, initialPath, onPick, onClose }) {
  const dispatch = useDispatch();
  const [path, setPath] = useState(initialPath || "/var/www");
  const [parent, setParent] = useState(null);
  const [dirs, setDirs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async (p) => {
    setLoading(true);
    setErr(null);
    const res = await dispatch(nginxBrowseDirs(projectId, serverId, p));
    setLoading(false);
    if (res?.status) {
      setPath(res.data.path);
      setParent(res.data.parent);
      setDirs(res.data.dirs || []);
    } else {
      // Keep showing where we were — a failed step shouldn't blank the list.
      setErr(res?.message || `Cannot read ${p}`);
    }
  }, [dispatch, projectId, serverId]);

  useEffect(() => { load(initialPath || "/var/www"); }, [load, initialPath]);

  const join = (base, name) => (base === "/" ? `/${name}` : `${base}/${name}`);

  return (
    <Modal show onHide={onClose} centered scrollable>
      <Modal.Header closeButton>
        <h6 className="m-0 d-inline-flex align-items-center gap-2">
          <HardDrive size={16} /> Choose a folder
        </h6>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-wrap gap-1 mb-2">
          {SHORTCUTS.map((s) => (
            <Button key={s} size="sm" variant="light" onClick={() => load(s)}
              style={{ fontFamily: "ui-monospace, monospace", fontSize: 11 }}>
              {s}
            </Button>
          ))}
        </div>

        <div className="mb-2 p-2 rounded" style={{ background: "var(--bg-raised, #f1f5f9)" }}>
          <code className="small">{path}</code>
        </div>

        {err && <div className="alert alert-warning py-2 small">{err}</div>}

        {loading ? (
          <div className="text-center py-3"><Spinner size="sm" animation="border" /></div>
        ) : (
          <div style={{ maxHeight: 320, overflow: "auto" }}>
            {parent && (
              <button type="button" className="btn btn-link btn-sm d-block text-start p-1 text-decoration-none"
                onClick={() => load(parent)}>
                <CornerLeftUp size={14} className="me-1" /> ..
              </button>
            )}
            {dirs.length === 0 && !parent && <div className="small text-muted">No sub-folders here.</div>}
            {dirs.length === 0 && parent && <div className="small text-muted">No sub-folders — pick this one.</div>}
            {dirs.map((d) => (
              <button key={d} type="button"
                className="btn btn-link btn-sm d-block text-start p-1 text-decoration-none"
                onClick={() => load(join(path, d))}
                style={{ fontFamily: "ui-monospace, monospace" }}>
                <Folder size={14} className="me-1" /> {d}
              </button>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={loading} onClick={() => { onPick(path); onClose(); }}>
          Use this folder
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
